import graphql from "babel-plugin-relay/macro";
import { useCallback, useMemo } from "react";
import { useFragment } from "react-relay";

import { VStack } from "@whoosh/design-system/src/components/primitive";

import { AppliedPunchPassesSection } from "./AppliedPunchPassesSection";
import { AvailablePunchPassesSection } from "./AvailablePunchPassesSection";
import type { AvailablePunchPassesSectionItem } from "./AvailablePunchPassesSection";
import { UnavailablePunchPassesSection } from "./UnavailablePunchPassesSection";
import {
  PunchPassListItem_PunchPass$key,
  PunchPassTemplateType,
} from "./__generated__/PunchPassListItem_PunchPass.graphql";
import {
  RoundPunchPassContent_Round$key,
  RoundPunchPassContent_Round$data,
} from "./__generated__/RoundPunchPassContent_Round.graphql";

export type RoundPunchPassContentProps = {
  roundRef: RoundPunchPassContent_Round$key;
  appliedRateId: string | null;
  appliedTransportId: string | null;
  onAppliedChange: (
    ratePunchPassId: string | null,
    transportPunchPassId: string | null
  ) => void;
};

function getRefForRateId(
  round: RoundPunchPassContent_Round$data,
  id: string | null
): PunchPassListItem_PunchPass$key | null {
  if (!id) return null;
  if (round.ratePunchPass?.id === id) return round.ratePunchPass;
  const selfValid = round.punchPassOptionsSelfValid ?? [];
  const othersValid = round.punchPassOptionsOthersValid ?? [];
  const allValid = [...selfValid, ...othersValid];
  const node = allValid.find((n) => n?.id === id);
  return node ?? null;
}

function getRefForTransportId(
  round: RoundPunchPassContent_Round$data,
  id: string | null
): PunchPassListItem_PunchPass$key | null {
  if (!id) return null;
  if (round.transportationPunchPass?.id === id)
    return round.transportationPunchPass;
  const selfValid = round.punchPassOptionsSelfValid ?? [];
  const othersValid = round.punchPassOptionsOthersValid ?? [];
  const allValid = [...selfValid, ...othersValid];
  const node = allValid.find((n) => n?.id === id);
  return node ?? null;
}

export const RoundPunchPassContent = ({
  roundRef,
  appliedRateId,
  appliedTransportId,
  onAppliedChange,
}: RoundPunchPassContentProps) => {
  const round = useFragment(RoundPunchPassContentFragment, roundRef);

  const appliedRefs = useMemo((): PunchPassListItem_PunchPass$key[] => {
    const refs: PunchPassListItem_PunchPass$key[] = [];
    const rateRef = getRefForRateId(round, appliedRateId);
    const transportRef = getRefForTransportId(round, appliedTransportId);
    if (rateRef) refs.push(rateRef);
    if (transportRef) refs.push(transportRef);
    return refs;
  }, [round, appliedRateId, appliedTransportId]);

  const handleRemoveRate = useCallback(() => {
    onAppliedChange(null, appliedTransportId);
  }, [appliedTransportId, onAppliedChange]);

  const handleRemoveTransport = useCallback(() => {
    onAppliedChange(appliedRateId, null);
  }, [appliedRateId, onAppliedChange]);

  const handleApplyToRate = useCallback(
    (id: string) => {
      onAppliedChange(id, appliedTransportId);
    },
    [appliedTransportId, onAppliedChange]
  );

  const handleApplyToTransport = useCallback(
    (id: string) => {
      onAppliedChange(appliedRateId, id);
    },
    [appliedRateId, onAppliedChange]
  );

  const handleRemove = useCallback(
    (id: string) => {
      if (round.ratePunchPass?.id === id || appliedRateId === id) {
        handleRemoveRate();
        return;
      }
      if (
        round.transportationPunchPass?.id === id ||
        appliedTransportId === id
      ) {
        handleRemoveTransport();
      }
    },
    [
      appliedRateId,
      appliedTransportId,
      round.ratePunchPass?.id,
      round.transportationPunchPass?.id,
      handleRemoveRate,
      handleRemoveTransport,
    ]
  );

  const selfValid = useMemo(
    () => round.punchPassOptionsSelfValid ?? [],
    [round.punchPassOptionsSelfValid]
  );
  const othersValid = useMemo(
    () => round.punchPassOptionsOthersValid ?? [],
    [round.punchPassOptionsOthersValid]
  );
  const allValid = useMemo(
    () => [...selfValid, ...othersValid],
    [selfValid, othersValid]
  );

  const allAvailableItems: AvailablePunchPassesSectionItem[] = useMemo(
    () =>
      allValid.flatMap((opt) => {
        if (
          opt == null ||
          opt.id === appliedRateId ||
          opt.id === appliedTransportId
        ) {
          return [];
        }
        const type = opt.punchPassTemplate?.type;
        return [
          {
            ref: opt,
            id: opt.id,
            hasSameTypeApplied:
              type === "RATE"
                ? appliedRateId != null
                : appliedTransportId != null,
          },
        ];
      }),
    [allValid, appliedRateId, appliedTransportId]
  );

  const unavailableRefs: readonly PunchPassListItem_PunchPass$key[] = useMemo(
    () =>
      (round.punchPassOptionsInvalid ??
        []) as unknown as PunchPassListItem_PunchPass$key[],
    [round.punchPassOptionsInvalid]
  );

  const handleApply = useCallback(
    (id: string, punchPassType?: PunchPassTemplateType) => {
      if (punchPassType === "RATE") {
        handleApplyToRate(id);
      } else if (punchPassType === "TRANSPORTATION") {
        handleApplyToTransport(id);
      }
    },
    [handleApplyToRate, handleApplyToTransport]
  );

  return (
    <VStack gap={2} backgroundColor="surface-secondary">
      <AppliedPunchPassesSection
        punchPassRefs={appliedRefs}
        onRemove={handleRemove}
      />
      <AvailablePunchPassesSection
        items={allAvailableItems}
        onApply={handleApply}
        onRemove={handleRemove}
      />
      <UnavailablePunchPassesSection punchPassRefs={unavailableRefs} />
    </VStack>
  );
};

const RoundPunchPassContentFragment = graphql`
  fragment RoundPunchPassContent_Round on Round {
    id
    player {
      fullName
    }
    ratePunchPass {
      id
      ...PunchPassListItem_PunchPass
    }
    transportationPunchPass {
      id
      ...PunchPassListItem_PunchPass
    }
    ... on MemberRound {
      punchPassOptionsSelfValid: punchPassOptions(
        valid: true
        appliesTo: SELF
      ) {
        id
        punchPassTemplate {
          type
        }
        ...PunchPassListItem_PunchPass
      }
      punchPassOptionsOthersValid: punchPassOptions(
        valid: true
        appliesTo: OTHERS
      ) {
        id
        punchPassTemplate {
          type
        }
        ...PunchPassListItem_PunchPass
      }
      punchPassOptionsInvalid: punchPassOptions(valid: false) {
        id
        punchPassTemplate {
          type
        }
        ...PunchPassListItem_PunchPass
      }
    }
    ... on CustomerRound {
      punchPassOptionsSelfValid: punchPassOptions(
        valid: true
        appliesTo: SELF
      ) {
        id
        punchPassTemplate {
          type
        }
        ...PunchPassListItem_PunchPass
      }
      punchPassOptionsOthersValid: punchPassOptions(
        valid: true
        appliesTo: OTHERS
      ) {
        id
        punchPassTemplate {
          type
        }
        ...PunchPassListItem_PunchPass
      }
      punchPassOptionsInvalid: punchPassOptions(valid: false) {
        id
        punchPassTemplate {
          type
        }
        ...PunchPassListItem_PunchPass
      }
    }
  }
`;
